import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';

class NetworkManager {
  private client: AxiosInstance;
  private token: string | null = null;
  private environment: string = 'dev';
  private emulateUser: string | null = null;

  constructor() {
    // 30s matches the API Gateway / Lambda timeout — a higher client timeout
    // just hangs past when the gateway has already given up. For dev, the
    // billing snapshot's slow cold-start path is mitigated by the local FS
    // cache (see apps/api/src/modules/billing/billing.cache.ts); after the
    // first fetch subsequent loads are <1s.
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '',
      timeout: 30_000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      config.headers['X-Environment'] = this.environment;
      if (this.emulateUser) {
        config.headers['X-Emulate-User'] = this.emulateUser;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.token = null;
          localStorage.removeItem('byte_id_token');
          localStorage.removeItem('byte_refresh_token');
          window.location.href = '/';
        }
        return Promise.reject(error);
      },
    );
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  setEnvironment(env: string): void {
    this.environment = env;
  }

  getEnvironment(): string {
    return this.environment;
  }

  setEmulateUser(yumId: string | null): void {
    this.emulateUser = yumId;
  }

  getEmulateUser(): string | null {
    return this.emulateUser;
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export const networkManager = new NetworkManager();
